import React, { useEffect, useRef, useState } from "react";
import { XMarkIcon } from "@heroicons/react/24/outline";
import {
  createTPRMAssessmentApi,
  getTPRMAssessmentApi,
  getTPRMSelectableTemplatesApi,
  getTPRMVendorServicesApi,
} from "../../../services/axios/AuthApis/TPRMApis";
import useTPRMDialog from "./useTPRMDialog";

const ASSESSMENT_TYPES = [
  "Initial",
  "Annual",
  "Ad-hoc",
  "Renewal",
  "Incident-triggered",
];

const inputClassName =
  "h-10 w-full rounded-lg border border-slate-300 bg-white px-3 text-sm text-slate-800 shadow-sm outline-none transition focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100";

const getErrorMessage = (value) => {
  if (Array.isArray(value)) return value.map(getErrorMessage).filter(Boolean).join(" ");
  if (typeof value === "string") return value;
  if (value && typeof value === "object") {
    return Object.values(value).map(getErrorMessage).filter(Boolean).join(" ");
  }
  return "";
};

const normalizeTemplates = (data) => {
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.results)) return data.results;
  return [];
};

const isValidEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

const AssessmentInitiationModal = ({ vendor, onClose, onCreated }) => {
  const [templates, setTemplates] = useState([]);
  const [loadingTemplates, setLoadingTemplates] = useState(true);
  const [templateError, setTemplateError] = useState("");
  const [templateReload, setTemplateReload] = useState(0);
  const [services, setServices] = useState([]);
  const [loadingServices, setLoadingServices] = useState(false);
  const [serviceError, setServiceError] = useState("");
  const [templateId, setTemplateId] = useState("");
  const [serviceId, setServiceId] = useState("");
  const [recipientEmail, setRecipientEmail] = useState(
    vendor.primary_contact?.email || "",
  );
  const [assessmentType, setAssessmentType] = useState("Initial");
  const [dueDate, setDueDate] = useState("");
  const [fieldErrors, setFieldErrors] = useState({});
  const [formError, setFormError] = useState("");
  const [creating, setCreating] = useState(false);
  const [createdAssessment, setCreatedAssessment] = useState(null);
  const [showDetails, setShowDetails] = useState(false);
  const [assessmentDetails, setAssessmentDetails] = useState(null);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [detailsError, setDetailsError] = useState("");
  const createInProgress = useRef(false);
  const dialogRef = useTPRMDialog({ onClose, closeOnEscape: !creating });

  useEffect(() => {
    let cancelled = false;
    const loadTemplates = async () => {
      setLoadingTemplates(true);
      setTemplateError("");
      try {
        const result = normalizeTemplates(
          await getTPRMSelectableTemplatesApi(),
        ).filter(
          (template) =>
            template.tenant_id === null ||
            template.tenant_id === undefined ||
            (vendor.tenant_id &&
              String(template.tenant_id) === String(vendor.tenant_id)),
        );
        if (!cancelled) {
          setTemplates(result);
          setTemplateId((current) =>
            result.some((item) => String(item.id) === current)
              ? current
              : String(result[0]?.id || ""),
          );
        }
      } catch (requestError) {
        if (!cancelled) {
          setTemplateError(
            getErrorMessage(requestError.response?.data) ||
              requestError.message ||
              "Unable to load questionnaire templates.",
          );
        }
      } finally {
        if (!cancelled) setLoadingTemplates(false);
      }
    };
    loadTemplates();
    return () => {
      cancelled = true;
    };
  }, [templateReload, vendor.tenant_id]);

  useEffect(() => {
    let cancelled = false;
    const loadServices = async () => {
      if (!vendor.vendor_id) return;
      setLoadingServices(true);
      setServiceError("");
      try {
        const records = [];
        let offset = 0;
        let hasNextPage = true;
        while (hasNextPage) {
          const result = await getTPRMVendorServicesApi({
            limit: 100,
            offset,
            vendor_id: vendor.vendor_id,
          });
          const page = Array.isArray(result)
            ? result
            : Array.isArray(result?.results)
              ? result.results
              : [];
          records.push(...page);
          hasNextPage = !Array.isArray(result) && Boolean(result?.next) && page.length > 0;
          offset += page.length;
        }
        if (!cancelled) {
          setServices(records);
          setServiceId((current) =>
            records.some((item) => String(item.id) === current)
              ? current
              : "",
          );
        }
      } catch (requestError) {
        if (!cancelled) {
          setServiceError(
            getErrorMessage(requestError.response?.data) ||
              requestError.message ||
              "Unable to load vendor services.",
          );
        }
      } finally {
        if (!cancelled) setLoadingServices(false);
      }
    };
    loadServices();
    return () => {
      cancelled = true;
    };
  }, [vendor.vendor_id]);

  const submit = async (event) => {
    event.preventDefault();
    if (createInProgress.current) return;

    const errors = {};
    const normalizedEmail = recipientEmail.trim();
    if (!templateId) errors.template = "Select an active questionnaire template.";
    if (!normalizedEmail || !isValidEmail(normalizedEmail)) {
      errors.recipient_email = "Enter a valid questionnaire recipient email.";
    }
    if (!ASSESSMENT_TYPES.includes(assessmentType)) {
      errors.assessment_type = "Select a supported assessment type.";
    }
    if (!vendor.id) errors.vendor = "This vendor cannot be identified for assessment.";
    if (!vendor.tenant_id) {
      errors.vendor = "This vendor has no assigned customer tenant and cannot be assessed.";
    }
    if (Object.keys(errors).length) {
      setFieldErrors(errors);
      return;
    }

    createInProgress.current = true;
    setCreating(true);
    setFieldErrors({});
    setFormError("");
    try {
      const payload = {
        vendor: vendor.id,
        template: templateId,
        recipient_email: normalizedEmail,
        assessment_type: assessmentType,
      };
      if (serviceId) payload.service = serviceId;
      if (dueDate) payload.due_date = dueDate;
      const result = await createTPRMAssessmentApi(payload);
      setCreatedAssessment(result);
      onCreated?.(result);
    } catch (requestError) {
      const responseData = requestError.response?.data;
      setFieldErrors({
        template: getErrorMessage(responseData?.template),
        recipient_email: getErrorMessage(responseData?.recipient_email),
        assessment_type: getErrorMessage(responseData?.assessment_type),
        service: getErrorMessage(responseData?.service),
        due_date: getErrorMessage(responseData?.due_date),
        vendor: getErrorMessage(responseData?.vendor),
      });
      setFormError(
        getErrorMessage(responseData?.detail) ||
          getErrorMessage(responseData?.tenant_id) ||
          requestError.message ||
          "Unable to create the assessment.",
      );
    } finally {
      createInProgress.current = false;
      setCreating(false);
    }
  };

  const loadAssessmentDetails = async () => {
    setShowDetails(true);
    setLoadingDetails(true);
    setDetailsError("");
    try {
      setAssessmentDetails(
        await getTPRMAssessmentApi(createdAssessment.assessment_id),
      );
    } catch (requestError) {
      setDetailsError(
        getErrorMessage(requestError.response?.data) ||
          requestError.message ||
          "Unable to load assessment details.",
      );
    } finally {
      setLoadingDetails(false);
    }
  };

  const details = assessmentDetails || createdAssessment || {};

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center overflow-y-auto bg-slate-950/60 p-4"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget && !creating) onClose();
      }}
    >
      <section
        aria-labelledby="assessment-initiation-title"
        aria-modal="true"
        className="relative my-auto w-full max-w-xl rounded-xl border border-slate-200 bg-white p-5 shadow-xl dark:border-slate-700 dark:bg-slate-900 sm:p-6"
        ref={dialogRef}
        role="dialog"
        tabIndex={-1}
      >
        <div className="mb-5 flex items-start justify-between gap-4">
          <div>
            <h2
              className="text-lg font-semibold text-slate-900 dark:text-white"
              id="assessment-initiation-title"
            >
              {showDetails
                ? "Assessment details"
                : createdAssessment
                  ? "Assessment created"
                  : "Initiate questionnaire"}
            </h2>
            <p className="mt-1 text-sm text-slate-600 dark:text-slate-400">
              {vendor.name}
            </p>
          </div>
          <button
            aria-label="Close assessment initiation"
            className="rounded-md p-1 text-slate-500 hover:bg-slate-100 hover:text-slate-700 disabled:opacity-50 dark:hover:bg-slate-800 dark:hover:text-slate-200"
            disabled={creating}
            onClick={onClose}
            title="Close assessment initiation"
            type="button"
          >
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>

        {showDetails ? (
          <div className="space-y-4">
            {loadingDetails && (
              <p className="text-sm text-slate-600 dark:text-slate-300" role="status">
                Loading assessment details…
              </p>
            )}
            {detailsError && (
              <p className="text-sm text-rose-700 dark:text-rose-300" role="alert">
                {detailsError}
              </p>
            )}
            {!loadingDetails && !detailsError && (
              <dl className="grid grid-cols-1 gap-3 rounded-lg bg-slate-50 p-4 text-sm dark:bg-slate-800 sm:grid-cols-2">
                <div>
                  <dt className="text-slate-500 dark:text-slate-400">Assessment ID</dt>
                  <dd className="mt-1 font-medium text-slate-900 dark:text-white">
                    {details.assessment_id}
                  </dd>
                </div>
                <div>
                  <dt className="text-slate-500 dark:text-slate-400">Status</dt>
                  <dd className="mt-1 font-medium text-slate-900 dark:text-white">
                    {details.status}
                  </dd>
                </div>
                <div>
                  <dt className="text-slate-500 dark:text-slate-400">Recipient</dt>
                  <dd className="mt-1 font-medium text-slate-900 dark:text-white">
                    {details.recipient_email}
                  </dd>
                </div>
                <div>
                  <dt className="text-slate-500 dark:text-slate-400">Assessment type</dt>
                  <dd className="mt-1 font-medium text-slate-900 dark:text-white">
                    {details.assessment_type}
                  </dd>
                </div>
                {details.due_date && (
                  <div>
                    <dt className="text-slate-500 dark:text-slate-400">Due date</dt>
                    <dd className="mt-1 font-medium text-slate-900 dark:text-white">
                      {details.due_date}
                    </dd>
                  </div>
                )}
                <div className="sm:col-span-2">
                  <dt className="text-slate-500 dark:text-slate-400">Questionnaire</dt>
                  <dd className="mt-1 font-medium text-slate-900 dark:text-white">
                    {details.template_snapshot?.name}
                  </dd>
                  <dd className="mt-1 text-xs text-slate-600 dark:text-slate-300">
                    {details.template_snapshot?.questions?.length || 0} questions snapshotted
                  </dd>
                </div>
              </dl>
            )}
            <div className="flex justify-end gap-2">
              <button
                className="rounded-lg border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 dark:border-slate-700 dark:text-slate-200 dark:hover:bg-slate-800"
                onClick={() => setShowDetails(false)}
                type="button"
              >
                Back
              </button>
              <button
                className="rounded-lg bg-blue-700 px-4 py-2 text-sm font-medium text-white hover:bg-blue-800"
                onClick={onClose}
                type="button"
              >
                Done
              </button>
            </div>
          </div>
        ) : createdAssessment ? (
          <div className="space-y-4">
            <p className="rounded-lg border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-800 dark:border-emerald-900 dark:bg-emerald-950/40 dark:text-emerald-200" role="status">
              Draft assessment created. No questionnaire invitation was sent.
            </p>
            <dl className="grid grid-cols-2 gap-3 rounded-lg bg-slate-50 p-4 text-sm dark:bg-slate-800">
              <div>
                <dt className="text-slate-500 dark:text-slate-400">Assessment ID</dt>
                <dd className="mt-1 font-medium text-slate-900 dark:text-white">
                  {createdAssessment.assessment_id}
                </dd>
              </div>
              <div>
                <dt className="text-slate-500 dark:text-slate-400">Status</dt>
                <dd className="mt-1 font-medium text-slate-900 dark:text-white">
                  {createdAssessment.status}
                </dd>
              </div>
            </dl>
            <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
              <button
                className="rounded-lg border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 dark:border-slate-700 dark:text-slate-200 dark:hover:bg-slate-800"
                onClick={onClose}
                type="button"
              >
                Done
              </button>
              <button
                className="rounded-lg bg-blue-700 px-4 py-2 text-sm font-medium text-white hover:bg-blue-800"
                onClick={loadAssessmentDetails}
                type="button"
              >
                View assessment
              </button>
            </div>
          </div>
        ) : (
          <form className="space-y-4" noValidate onSubmit={submit}>
            {formError && (
              <p
                className="rounded-lg border border-rose-200 bg-rose-50 p-3 text-sm text-rose-700 dark:border-rose-900 dark:bg-rose-950/40 dark:text-rose-300"
                role="alert"
              >
                {formError}
              </p>
            )}
            {fieldErrors.vendor && (
              <p className="text-sm text-rose-600 dark:text-rose-400" role="alert">
                {fieldErrors.vendor}
              </p>
            )}
            {loadingTemplates ? (
              <p className="text-sm text-slate-600 dark:text-slate-300" role="status">
                Loading active questionnaires…
              </p>
            ) : templateError ? (
              <div className="space-y-2">
                <p className="text-sm text-rose-700 dark:text-rose-300" role="alert">
                  {templateError}
                </p>
                <button
                  className="text-sm font-medium text-blue-700 hover:underline dark:text-blue-300"
                  onClick={() => setTemplateReload((value) => value + 1)}
                  type="button"
                >
                  Retry loading questionnaires
                </button>
              </div>
            ) : templates.length === 0 ? (
              <p className="rounded-lg border border-dashed border-slate-300 p-4 text-sm text-slate-600 dark:border-slate-700 dark:text-slate-300">
                No active questionnaires are available for this vendor's tenant.
              </p>
            ) : (
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300">
                Active questionnaire <span aria-hidden="true">*</span>
                <select
                  aria-label="Questionnaire template"
                  aria-invalid={Boolean(fieldErrors.template)}
                  aria-describedby={
                    fieldErrors.template ? "assessment-template-error" : undefined
                  }
                  className={`${inputClassName} mt-1`}
                  onChange={(event) => setTemplateId(event.target.value)}
                  required
                  value={templateId}
                >
                  <option value="">Select a questionnaire</option>
                  {templates.map((template) => (
                    <option key={template.id} value={String(template.id)}>
                      {template.name} (v{template.template_version || 1})
                    </option>
                  ))}
                </select>
                {fieldErrors.template && (
                  <span
                    className="mt-1 block text-xs text-rose-600 dark:text-rose-400"
                    id="assessment-template-error"
                  >
                    {fieldErrors.template}
                  </span>
                )}
              </label>
            )}
            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300">
              Questionnaire recipient email <span aria-hidden="true">*</span>
              <input
                aria-label="Questionnaire recipient email"
                aria-invalid={Boolean(fieldErrors.recipient_email)}
                aria-describedby={
                  fieldErrors.recipient_email
                    ? "assessment-recipient-error"
                    : undefined
                }
                autoComplete="email"
                className={`${inputClassName} mt-1`}
                maxLength={254}
                onChange={(event) => setRecipientEmail(event.target.value)}
                required
                type="email"
                value={recipientEmail}
              />
              {fieldErrors.recipient_email && (
                <span
                  className="mt-1 block text-xs text-rose-600 dark:text-rose-400"
                  id="assessment-recipient-error"
                >
                  {fieldErrors.recipient_email}
                </span>
              )}
            </label>
            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300">
              Assessment type
              <select
                aria-label="Assessment type"
                aria-invalid={Boolean(fieldErrors.assessment_type)}
                aria-describedby={
                  fieldErrors.assessment_type
                    ? "assessment-type-error"
                    : undefined
                }
                className={`${inputClassName} mt-1`}
                onChange={(event) => setAssessmentType(event.target.value)}
                value={assessmentType}
              >
                {ASSESSMENT_TYPES.map((type) => (
                  <option key={type} value={type}>
                    {type}
                  </option>
                ))}
              </select>
              {fieldErrors.assessment_type && (
                <span
                  className="mt-1 block text-xs text-rose-600 dark:text-rose-400"
                  id="assessment-type-error"
                >
                  {fieldErrors.assessment_type}
                </span>
              )}
            </label>
            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300">
              Related service (optional)
              <select
                aria-label="Related vendor service"
                className={`${inputClassName} mt-1`}
                disabled={loadingServices || creating}
                onChange={(event) => setServiceId(event.target.value)}
                value={serviceId}
              >
                <option value="">
                  {loadingServices ? "Loading services..." : "No service selected"}
                </option>
                {services.map((service) => (
                  <option key={service.id} value={service.id}>
                    {service.service_name} ({service.service_id}) - {service.business_criticality}
                  </option>
                ))}
              </select>
              {serviceError && (
                <span className="mt-1 block text-xs text-rose-700 dark:text-rose-300" role="alert">
                  {serviceError}
                </span>
              )}
              {fieldErrors.service && (
                <span className="mt-1 block text-xs text-rose-700 dark:text-rose-300" role="alert">
                  {fieldErrors.service}
                </span>
              )}
            </label>
            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300">
              Due date (optional)
              <input
                aria-label="Assessment due date"
                aria-invalid={Boolean(fieldErrors.due_date)}
                aria-describedby={
                  fieldErrors.due_date ? "assessment-due-date-error" : undefined
                }
                className={`${inputClassName} mt-1`}
                onChange={(event) => setDueDate(event.target.value)}
                type="date"
                value={dueDate}
              />
              {fieldErrors.due_date && (
                <span
                  className="mt-1 block text-xs text-rose-600 dark:text-rose-400"
                  id="assessment-due-date-error"
                >
                  {fieldErrors.due_date}
                </span>
              )}
            </label>
            <div className="rounded-lg bg-slate-50 p-3 text-xs text-slate-600 dark:bg-slate-800 dark:text-slate-300">
              Creating this assessment saves a Draft only. It does not send an invitation or approve the vendor.
            </div>
            {!vendor.tenant_id && (
              <p className="text-sm text-rose-700 dark:text-rose-300" role="alert">
                This vendor has no assigned customer tenant and cannot be assessed.
              </p>
            )}
            <div className="flex flex-col-reverse gap-2 pt-2 sm:flex-row sm:justify-end">
              <button
                className="rounded-lg border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50 dark:border-slate-700 dark:text-slate-200 dark:hover:bg-slate-800"
                disabled={creating}
                onClick={onClose}
                type="button"
              >
                Cancel
              </button>
              <button
                className="rounded-lg bg-blue-700 px-4 py-2 text-sm font-medium text-white hover:bg-blue-800 disabled:cursor-not-allowed disabled:opacity-50"
                disabled={
                  creating ||
                  loadingTemplates ||
                  Boolean(templateError) ||
                  !templates.length ||
                  !vendor.id ||
                  !vendor.tenant_id
                }
                type="submit"
              >
                {creating ? "Creating draft..." : "Review and create draft"}
              </button>
            </div>
          </form>
        )}
      </section>
    </div>
  );
};

export default AssessmentInitiationModal;
